<!--
  Component: ElementOverallRating

  This component creates an overall Framework Element rating section inside the
  tabbed content section.

  Includes:
  - Large VizFourBarRating for the current Framework element, and matching
      text description
  - Descriptive text about the element that varies based on element and
      selected school type (from store).

  Props:
  - elementCode - two-letter abbreviation: SA, RI, CT, SE, ES, SF, TR
-->

<template>
  <div class="element-overall-rating">
    <h3>{{ sectionHeading }}</h3>
    <div class="rating-content">
      <div v-if="!isSelectedSchoolPK" class="rating-col">
        <template v-if="!isCovidYear">
          <div class="rating-description">{{ elementRatingDescription }}</div>
          <VizFourBarRating class="large" :rating="elementRating"/>
        </template>
        <template v-else>
          <div class="na-description">N/A</div>
        </template>
      </div>
      <div class="description-col">
        <div class="description main">
          <template v-if="selectedSchoolType=='Pre-K'">
            <template v-if="elementCode=='RI'">
              <div class="efq-desc">EFQ: Advance play-based learning and responsive instruction.</div>
              <div>This section looks at how teaching teams differentiate strategies and activities to meet the individualized needs of all children in the classroom, and provide opportunities for children to demonstrate learning in multiple ways.</div>
            </template>
            <template v-else-if="elementCode=='CT'">
              <div class="efq-desc">EFQ: Work collaboratively towards continuous quality improvement.</div>
              <div>This section looks at how well teaching teams work together to improve the program.</div>
            </template>
            <template v-else-if="elementCode=='SE'">
              <div class="efq-desc">EFQ: Create safe and positive environments.</div>
              <div>This section looks at how well children are respected, valued, and supported to meet high expectations by program staff.</div>
            </template>
            <template v-else-if="elementCode=='ES'">
              <div class="efq-desc">EFQ: Demonstrate strategic leadership.</div>
              <div>This section looks at how well the pre-K program leadership inspires the pre-K community with a clear vision for instruction and family engagement.</div>
            </template>
            <template v-else-if="elementCode=='SF'">
              <div class="efq-desc">EFQ: Promote families’ roles as primary caregivers, teachers, and advocates.</div>
              <div>This section looks at how well the pre-K program forms effective partnerships with families and outside organizations to support families in meeting children's needs.</div>
            </template>
            <template v-else-if="elementCode=='TR'">
              <div class="efq-desc">EFQ: Respect and value differences.</div>
              <div>This section looks at whether relationships between administrators, educators, students, and families are based on trust and respect.</div>
            </template>
          </template>
          <template v-else>
            <template v-if="elementCode=='RI'">The <b>Rigorous Instruction</b> rating looks at how well curriculum and instruction are designed to engage students, foster critical thinking skills, and are aligned to New York State’s learning standards.</template>
            <template v-else-if="elementCode=='CT'">The <b>Collaborative Teachers</b> rating looks at how well teachers participate in opportunities to develop, grow, and contribute to the continuous improvement of the school community.</template>
            <template v-else-if="elementCode=='SE'">The <b>Supportive Environment</b> rating looks at how well the school establishes a culture where students feel safe, challenged to grow, and supported to meet high expectations.</template>
            <template v-else-if="elementCode=='ES'">The <b>Effective School Leadership</b> rating looks at how well school leadership inspires the school community with a clear instructional vision and effectively distributes leadership to realize this vision.</template>
            <template v-else-if="elementCode=='SF'">The <b>Strong Family-Community Ties</b> rating looks at how well the school forms effective partnerships with families and outside organizations to improve the school.</template>
            <template v-else-if="elementCode=='TR'">The <b>Trust</b> rating looks at whether relationships between school leaders, teachers, students, and families are based on trust and respect.</template>
            <template v-else-if="elementCode=='SA'">
              <template v-if="selectedSchoolType=='ES'">The <b>Student Achievement</b> rating looks at this school's State test results, including student growth and performance; how students performed in core courses; and how well students are prepared for middle school.</template>
              <template v-else-if="['HS','HST','YABC'].indexOf(selectedSchoolType)>-1">The <b>Student Achievement</b> rating looks at students' progress towards graduation, graduation rates, college and career readiness, and post-secondary enrollment.</template>
              <template v-else-if="['K-8','MS'].indexOf(selectedSchoolType)>-1">The <b>Student Achievement</b> rating looks at this school's State test results, including student growth and performance; how students performed in core courses; and how well students are prepared for high school.</template>
            </template>
          </template>
        </div>

        <div v-if="!isSelectedSchoolPK" class="description sub">
          <template v-if="isCovidYear">
            <span class="covid-sub">Due to COVID-19, ratings are not available for any schools for the {{ schoolYearRange }} school year.</span>
          </template>
          <template v-else>
            <template v-if="elementCode=='SA'">The overall section rating is a combination of many Student Achievement measures, including those presented here.</template>
            <template v-else-if="elementCode=='SE'">
              <template v-if="['ES','K-8','MS','HS','EC'].indexOf(selectedSchoolType)>-1">This section rating combines the results from <TheQRReportLink :qualityReviewURL="qualityReviewURL"/>, <TheSchoolSurveyLink :guideURL="guideURL"/>, and moving students with special needs to <b>less restrictive environments</b>, as well as chronic absenteeism.</template>
              <template v-else-if="['HST','D75'].indexOf(selectedSchoolType)>-1">This section rating combines the results from <TheQRReportLink :qualityReviewURL="qualityReviewURL"/>, <TheSchoolSurveyLink :guideURL="guideURL"/>, and moving students with special needs to <b>less restrictive environments</b>, as well as attendance.</template>
              <template v-else-if="['YABC'].indexOf(selectedSchoolType)>-1">This section rating takes into account <TheSchoolSurveyLink :guideURL="guideURL"/>, and moving students with special needs to <b>less restrictive environments</b>, as well as attendance.</template>
            </template>
            <template v-else-if="selectedSchoolType=='YABC'||elementCode=='TR'">This section rating is based on the results of <TheSchoolSurveyLink :guideURL="guideURL"/>.</template>
            <template v-else>This section rating combines the results from <TheQRReportLink :qualityReviewURL="qualityReviewURL"/> and <TheSchoolSurveyLink :guideURL="guideURL"/>.</template>
          </template>
        </div>

        <div class="footnotes">
          <template v-if="elementCode=='SA'">
            <FrameworkRatingFootnote :footnote-type="FR_FOOTNOTE_TYPES.SUPPRESS_SA"/>
            <FrameworkRatingFootnote :footnote-type="FR_FOOTNOTE_TYPES.OPT_OUT"/>
          </template>
          <template v-else-if="elementCode=='SE'">
            <FrameworkRatingFootnote :footnote-type="FR_FOOTNOTE_TYPES.SUPPRESS_SE"/>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import Component from 'vue-class-component'

import { NS_REPORT_GROUP, NS_CURRENT_REPORT } from '../const'
import { frElementNameForCode, valIsMissing } from '@/utils/data'

import LinkExternalUrl from '@/components/LinkExternalUrl'
import VizFourBarRating, { descriptionForRating } from '@/components/VizFourBarRating'
import FrameworkRatingFootnote, { FR_FOOTNOTE_TYPES } from '../FrameworkRatingFootnote'

// Local Component definitions
const TheQRReportLink = {
  props: { qualityReviewURL: String },
  components: { LinkExternalUrl },
  template: '<span>the <LinkExternalUrl :url="qualityReviewURL" :showPrintLink="false">Quality Review Report (PDF)</LinkExternalUrl></span>'
}
const TheSchoolSurveyLink = {
  props: { guideURL: String },
  components: { LinkExternalUrl },
  template: '<span>the NYC School Survey (available in the <LinkExternalUrl :url="guideURL"  :showPrintLink="false">School Quality Guide</LinkExternalUrl>)</span>'
}

@Component({
  props: {
    elementCode: String
  },
  computed: {
    ...mapGetters(NS_REPORT_GROUP, [
      'schoolYearRange'
    ]),
    ...mapGetters(NS_CURRENT_REPORT, [
      'selectedSchoolType',
      'isSelectedSchoolPK',
      'isSchoolDataLoaded',
      'elementRatingForCode',
      'noRatingTextForCode',
      'qualityReviewURL',
      'guideURL',
      'isCovidYear'
    ])
  },
  components: {
    TheQRReportLink,
    TheSchoolSurveyLink,
    VizFourBarRating,
    FrameworkRatingFootnote
  }
})
export default class ElementOverallRating extends Vue {
  FR_FOOTNOTE_TYPES = FR_FOOTNOTE_TYPES

  /* Getters */

  get sectionHeading () {
    return this.isSelectedSchoolPK ? this.elementName : `Overall Rating for ${this.elementName}`
  }
  get elementName () {
    return frElementNameForCode(this.elementCode)
  }
  get elementRating () {
    return this.elementRatingForCode(this.elementCode)
  }
  get isMissingRating () {
    return valIsMissing(this.elementRating)
  }
  get elementRatingDescription () {
    // Show blank if data not loaded yet, No Rating if data is missing
    const noRatingText = this.noRatingTextForCode(this.elementCode)
    return this.isSchoolDataLoaded && this.isMissingRating
      ? noRatingText : descriptionForRating(this.elementRating)
  }
}
</script>

<style lang="scss">
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import 'src/styles/const.scss';
@import '../css/const.scss';
.rating-content {
  display: flex;
  > div {
    display: inline-block;
  }
  .rating-col {
    margin-right: 24px;
    .rating-description {
      min-height: 24px;
      font-size: 20px;
      font-weight: bold;
      margin-bottom: 4px;
    }
    .viz-four-bar-rating {
      display: block;
    }
  }
  .na-description {
    font-size: 30px;
    font-weight: bold;
    min-width: 104px;
    text-align: center;
    padding-top: 8px;
    color: $alert-red-color;
  }
  .description-col {
    flex-grow: 1;
  }
}
.description.main {
  font-size: 17px;
  @media print {
    font-size: 15px;
  }
}
.description.sub {
  margin-bottom: 0px;
  span.covid-sub {
    font-size: 17px;
    font-weight: bold;
    color: $alert-red-color;
  }
}
.efq-desc {
  margin-bottom: 1em;
}

/* Responsive rules */

@media screen and (max-width: $screen-width-1-column-medium) {
.description.main {
  font-size: 15px;
}
}
@media screen and (max-width: $screen-width-1-column-small) {
.rating-col {
  margin-bottom: 12px;
}
.rating-content {
  display: block;
  > div {
    display: block;
  }
  .rating-col .rating-description,
  .rating-col .viz-four-bar-rating.no-rating {
    display: inline-block;
  }
}
}
</style>



// WEBPACK FOOTER //
// src/components/sqs/2022/tab-mods/ElementOverallRating.vue